import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { agentDefinitionsApi } from '@/lib/api/agent-definitions.api';

import type {
  CreateAgentDefinitionDto,
  UpdateAgentDefinitionDto,
  UpsertConfigurationAgentsDto,
} from '@repo/shared-types';

export function useAgentDefinitions() {
  return useQuery({
    queryKey: ['agent-definitions'],
    queryFn: () => agentDefinitionsApi.getAgentDefinitions(),
  });
}

export function useAgentDefinition(id: string) {
  return useQuery({
    queryKey: ['agent-definitions', id],
    queryFn: () => agentDefinitionsApi.getAgentDefinition(id),
    enabled: !!id,
  });
}

export function useCreateAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgentDefinitionDto) => agentDefinitionsApi.createAgentDefinition(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
    },
  });
}

export function useUpdateAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentDefinitionDto }) =>
      agentDefinitionsApi.updateAgentDefinition(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
      queryClient.invalidateQueries({ queryKey: ['agent-definitions', id] });
    },
  });
}

export function useDeleteAgentDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentDefinitionsApi.deleteAgentDefinition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
    },
  });
}

export function useConfigurationAgents(configId: string) {
  return useQuery({
    queryKey: ['configuration-agents', configId],
    queryFn: () => agentDefinitionsApi.getConfigurationAgents(configId),
    enabled: !!configId,
  });
}

export function useUpsertConfigurationAgents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: UpsertConfigurationAgentsDto }) =>
      agentDefinitionsApi.upsertConfigurationAgents(configId, data),
    onSuccess: (_, { configId }) => {
      queryClient.invalidateQueries({ queryKey: ['configuration-agents', configId] });
    },
  });
}
